// Hideo - the profile picker (ProfilePicker.vue: full screen at start-up and from "Switch profile") and the profile card
// (ProfileEditDialog.vue: create / edit a profile), switching profile, the profile shown at the top and in the page title,
// deleting a profile and the profile info of a loaded file.
// Their open state is in the dialogs store ('picker', 'profileEdit'); the logic without UI in src/features/profiles.ts.
// switchProfile and renderProfileUi are ported from the legacy profile/profiles.js (verbatim copy in
// tests/fixtures/legacy/profiles.js, golden tests in tests/switch-profile.test.ts); the saving they touch (the legacy data
// folder, the profile list's copy there, the warning dot) is src/features/storage/, the online check
// src/features/cloud/autosync.ts.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { t } from '../i18n'
import { dataFile, lsKey } from '../data/profiles'
import type { Profile } from '../data/types'
import { createFirstProject, freeProjectColor } from '../features/projects'
import { saveAppData } from '../features/storage/save'
import { applyProfileInfo, forgetProfileLocal, removeProfile, writeProfile, type ProfileForm } from '../features/profiles'
import { takeInitialPage } from '../navigation'
import { FOLDER, readFolderJson, saveProfiles, writeFolder } from '../features/storage/folder'
import { refreshWarn, setSync } from '../features/storage/sync'
import { useSyncStore } from './sync'
import { cloudAfterProfile, queueCloudSync } from '../features/cloud/autosync'
import { uiConfirm } from './notices'
import { useTourStore } from './tour'
import { useDataStore } from './data'
import { useDialogsStore } from './dialogs'
import { useProjectsStore } from './projects'
import { useSettingsStore } from './settings'
import { useUiStore } from './ui'
import { useCloudStore } from './cloud'
import { app } from '../features/app'

export const usePickerStore = defineStore('picker', () => {
  const store = useDataStore()
  const dialogs = useDialogsStore()

  /** A profile was chosen in this session: until then a legal page is shown on its own, without the app around it
   *  (src/features/legal.ts) */
  const entered = ref(false)
  /** A new profile created with its project's name (profile card): the project is created as soon as the profile is
   *  entered, so the "first project" dialog does not appear */
  let pendingProject: { profileId: string; name: string } | null = null
  function createPendingProject(): void {
    const p = pendingProject
    if (!p || store.profiles.current !== p.profileId) return
    pendingProject = null
    const d = store.getData()
    if (createFirstProject(d, p.name, freeProjectColor(d))) saveAppData(d)
  }

  /** Shows the profile picker (at start-up, and from "Switch profile"); its entrance animation starts again */
  function show() { dialogs.show('picker', {}) }
  const isOpen = () => dialogs.isOpen('picker')

  /** A profile chosen in the picker: the app starts with it (or switches to it), then the address the app was opened
   *  with (once) or the start page, the online check, the first project and the guided tour — in this order */
  async function pick(id: string) {
    dialogs.hide('picker')
    entered.value = true
    if (!app.booted) { store.profiles.current = id; saveProfiles(); app.boot() }
    else if (id !== store.profiles.current) await switchTo(id)
    createPendingProject()
    app.switchTab(takeInitialPage() || (useSettingsStore().values.startTab as never), true)
    void cloudAfterProfile()   // most recent online version and changes still to save (autosync.ts)
    await useProjectsStore().ensureFirst()   // profile without projects: the first one is created (FirstProjectDialog.vue)
    useTourStore().maybeStart()   // guided tour the first time on this device (src/stores/tour.ts)
  }

  /** "Switch profile" in the Profiles window: back to the profile picker */
  function change() { useCloudStore().closeProfiles(); show() }

  /** Opens the profile card: an existing profile, or a new one (null; from the "+" circle of the picker) */
  function edit(id: string | null) {
    if (id && !store.profiles.list.some(x => x.id === id)) return
    dialogs.show('profileEdit', { id: id || null })
  }
  function closeEdit() { dialogs.hide('profileEdit') }

  /** Saves the profile card. Returns what is wrong with the name ('empty', 'taken'; nothing is saved), or null.
   *  A new profile is entered at once (from the picker or from the Profiles window); `projectName` (new profile only)
   *  becomes its first project */
  function save(id: string | null, f: ProfileForm, projectName = ''): 'empty' | 'taken' | null {
    const r = writeProfile(store.profiles.list, id, f)
    if ('error' in r) {
      if (r.error === 'missing') closeEdit()
      return r.error === 'missing' ? null : r.error
    }
    const p = r.profile
    saveProfiles(); closeEdit()
    if (r.isNew) {
      if (projectName.trim()) pendingProject = { profileId: p.id, name: projectName }
      if (isOpen()) void pick(p.id)
      else void switchTo(p.id)
      return null
    }
    // Name, photo, bio and socials travel in the profile's online file: they must be saved too
    setSync(p.id, { dirty: true }); queueCloudSync()
    renderUi()
    return null
  }

  /** Deletes a profile (never the active one) after asking: its data and save state in this browser, and its file in
   *  the legacy data folder. The files on Google Drive and the backups stay */
  async function remove(id: string) {
    const p = store.profiles.list.find(x => x.id === id)
    if (!p || id === store.profiles.current) return
    if (!(await uiConfirm(t('Delete the profile "{name}" and all its data (ideas, videos, creators, sources, scripts) from this device?\n\nThe files already saved on Google Drive and the daily backups are not touched.', { name: p.name }), { ok: t('Delete'), danger: true }))) return
    if (!removeProfile(store.profiles, id)) return
    forgetProfileLocal(id, useSyncStore().SYNC)
    if (FOLDER.ok) { try { await FOLDER.dirHandle?.removeEntry(dataFile(id)) } catch { /* already gone */ } }
    saveProfiles(); renderUi()
  }

  /** Name, photo, bio and socials saved in a loaded file (applyLoadedFile, src/features/storage/files.ts) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function applyInfo(p: Profile, info: any) {
    applyProfileInfo(store.profiles.list, p, info)
    saveProfiles(); renderUi()
  }

  /** The active profile at the top right and on the phone (TopControls.vue, MobileBar.vue), the Profiles window and the
   *  page title (the profile's name only with several profiles; same in every language) */
  function renderUi(): void {
    const p = store.currentProfile()
    if (!p) return
    refreshWarn(); useUiStore().refresh()
    document.title = store.profiles.list.length > 1 ? `Hideo · ${p.name}` : 'Hideo'
  }

  /** Switches the active profile (Profiles window, a new profile, the picker): the last changes of the profile being left
   *  are written first; with the legacy data folder the profile's data comes from its file there (or goes into it). Then
   *  everything is redrawn and the Profiles window closes; from the Profiles window (not from the picker, where pick does
   *  it) the online check and the first project follow */
  async function switchTo(id: string): Promise<void> {
    if (id === store.profiles.current || !store.profiles.list.some(p => p.id === id)) return
    if (FOLDER.writeTimer) { clearTimeout(FOLDER.writeTimer); await writeFolder() }
    store.profiles.current = id; saveProfiles()
    store.setData(null)
    if (FOLDER.ok) {
      const existing = await readFolderJson(dataFile(id)) as { videos?: unknown } | null
      if (existing && existing.videos) store.setData(existing)
      else {
        // Profile without a file in the folder: start from the browser data (if any) and write it into the folder
        store.getData(); await writeFolder()
        if (FOLDER.ok) { try { localStorage.removeItem(lsKey(id)) } catch { /* blocked storage */ } }
      }
    }
    store.getData()
    createPendingProject()
    app.renderAll(); renderUi(); refreshWarn()
    useCloudStore().closeProfiles()
    if (!isOpen()) { void cloudAfterProfile(); void useProjectsStore().ensureFirst() }
  }

  return { entered, show, isOpen, pick, change, edit, closeEdit, save, remove, applyInfo, renderUi, switchTo }
})
